import type { ChartSeries } from './StackedBarChart';
import type { ChartScale } from './LinePlot';
import { ChartAxis } from './ChartAxis';

// Same fade a highlighted legend entry applies elsewhere (LinePlot) - see
// its own DIMMED comment.
const DIMMED = 0.2;

// Bar mode for metrics that don't add up across signals (latest / average /
// min / max): stacking those would sum numbers that mean nothing together, so
// each bucket shows one bar per signal side by side instead. (This used to
// fall back to a line chart, which read as "bar mode is broken".)
export function GroupedBarChart({
  series,
  height = 180,
  scale = 'shared',
  highlightKey = null,
  selected = [],
  onSelect,
}: {
  series: ChartSeries[];
  height?: number;
  scale?: ChartScale;
  highlightKey?: string | null;
  // The pinned (clicked) bar, by series name + bucket index.
  selected?: { key: string; index: number }[];
  onSelect?: (key: string, index: number) => void;
}) {
  const bucketCount = series[0]?.values.length ?? 0;
  const maxOf = (vals: number[]) => Math.max(...vals.filter(Number.isFinite), 1);
  const sharedMax = maxOf(series.flatMap((s) => s.values));

  return (
    <ChartAxis max={sharedMax} height={height}>
      <div className="flex items-end gap-2.5 w-full h-full">
        {Array.from({ length: bucketCount }, (_, i) => (
          <div key={i} className="flex-1 h-full flex items-end gap-px">
            {series.map((s) => {
              const value = s.values[i] ?? NaN;
              const isSelected = selected.some((x) => x.key === s.name && x.index === i);
              return (
                <button
                  key={s.name}
                  type="button"
                  disabled={!onSelect}
                  onClick={() => onSelect?.(s.name, i)}
                  className={`flex-1 rounded-t-md ${onSelect ? 'cursor-pointer' : ''} ${
                    isSelected ? 'ring-2 ring-offset-1 ring-black/60 dark:ring-white/70' : ''
                  }`}
                  style={{
                    height: Number.isFinite(value) ? `${value > 0 ? Math.max(8, (value / (scale === 'each' ? maxOf(s.values) : sharedMax)) * 100) : 2}%` : '0%',
                    background: s.color,
                    opacity: highlightKey != null && s.name !== highlightKey ? DIMMED : 1,
                  }}
                />
              );
            })}
          </div>
        ))}
      </div>
    </ChartAxis>
  );
}
